import { useState, useEffect } from "react";
import { processData } from "./processor.js";
import { Person } from "./Person.js";
import { FilePicker } from "./FilePicker.js";

export function App() {
  const [inactiveRegulars, setInactiveRegulars] = useState([]);
  const [hiddenIds, setHiddenIds] = useState([]);
  const [selectedSession, setSelectedSession] = useState("All");

  // Load hiddenIds from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("hiddenInactiveRegulars");
    if (stored) {
      setHiddenIds(JSON.parse(stored));
    }
  }, []);

  function toggleHide(personId) {
    setHiddenIds((prev) => {
      const newHiddenIds = prev.includes(personId)
        ? prev.filter((id) => id !== personId)
        : [...prev, personId];
      localStorage.setItem(
        "hiddenInactiveRegulars",
        JSON.stringify(newHiddenIds)
      );
      return newHiddenIds;
    });
  }

  function handleData(csvData) {
    if (!csvData) return;
    const result = processData(csvData);

    const sorted = (result.inactiveRegulars || [])
      .slice()
      .sort((a, b) => b.weeksSinceLastCheckin - a.weeksSinceLastCheckin);
    setInactiveRegulars(sorted);
  }

  // Compute unique sessions for dropdown
  const sessions = Array.from(
    new Set(inactiveRegulars.map((p) => p.session).filter(Boolean))
  ).sort();

  // Filter by session and hidden state
  const filteredRegulars = inactiveRegulars.filter(
    (person) => selectedSession === "All" || person.session === selectedSession
  );
  const visibleRegulars = filteredRegulars.filter(
    (person) => !hiddenIds.includes(person.personId)
  );
  const hiddenRegulars = filteredRegulars.filter((person) =>
    hiddenIds.includes(person.personId)
  );

  return (
    <>
      <div className="max-w-3xl mx-auto py-8 px-4">
        <h1 className="mb-6 text-3xl font-bold text-gray-800">
          Attendance Dashboard
        </h1>
        <p className="mb-2 text-gray-700">
          This tool will process the{" "}
          <span className="font-mono bg-gray-100 px-1 rounded">
            All Check-ins.csv
          </span>{" "}
          file and identify regular attendees who have not checked in on any of
          the last 3 Sundays.
        </p>
        <p className="mb-6 text-gray-700">
          Regular attendees are defined as those who have checked in 4 or more
          times.
        </p>

        <FilePicker onData={handleData}></FilePicker>

        {/* Session filter dropdown */}
        {sessions.length > 0 && (
          <div className="mb-6">
            <label
              htmlFor="sessionFilter"
              className="block mb-2 font-medium text-gray-700"
            >
              Filter by Session
            </label>
            <select
              id="sessionFilter"
              className="block w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={selectedSession}
              onChange={(e) => setSelectedSession(e.target.value)}
            >
              <option value="All">All Sessions</option>
              {sessions.map((session) => (
                <option key={session} value={session}>
                  {session}
                </option>
              ))}
            </select>
          </div>
        )}

        <div id="results">
          {visibleRegulars.length > 0 && (
            <ul className="divide-y divide-gray-200 bg-white rounded shadow">
              <li className="px-4 py-2 bg-gray-100 font-semibold text-gray-700 rounded-t">
                Inactive Regular Attendees ({visibleRegulars.length})
              </li>

              {visibleRegulars.map((person, idx) => (
                <Person
                  key={person.personId}
                  person={person}
                  toggleHide={toggleHide}
                  isHidden={false}
                ></Person>
              ))}
            </ul>
          )}

          {hiddenRegulars.length > 0 && (
            <details className="mt-6">
              <summary className="cursor-pointer text-gray-600 hover:underline">
                {hiddenRegulars.length} hidden attendee
                {hiddenRegulars.length > 1 ? "s" : ""}
              </summary>
              <ul className="divide-y divide-gray-200 bg-gray-50 rounded shadow mt-2">
                {hiddenRegulars.map((person) => (
                  <Person
                    key={person.personId}
                    person={person}
                    toggleHide={toggleHide}
                    isHidden={true}
                  ></Person>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </>
  );
}
